import React from 'react';
import { FigureCard } from './FigureCard';
// 80°C trial data
const data = [{
  t: 0,
  p: 0.176
}, {
  t: 10,
  p: 0.336
}, {
  t: 20,
  p: 0.504
}, {
  t: 30,
  p: 0.706
}, {
  t: 40,
  p: 0.81
}, {
  t: 50,
  p: 0.865
}, {
  t: 60,
  p: 0.828
}, {
  t: 70,
  p: 0.774
}, {
  t: 80,
  p: 0.723
}, {
  t: 90,
  p: 0.672
}, {
  t: 100,
  p: 0.624
}, {
  t: 110,
  p: 0.563
}, {
  t: 120,
  p: 0.504
}];
const trapAreas = [2.56, 4.2, 6.05, 7.58, 8.38, 8.47, 8.01, 7.49, 6.98, 6.48, 5.94, 5.34];
// Chart geometry (SVG)
const W = 900;
const H = 420;
const M = {
  top: 30,
  right: 30,
  bottom: 60,
  left: 70
};
const innerW = W - M.left - M.right;
const innerH = H - M.top - M.bottom;
const xMin = 0,
  xMax = 120;
const yMin = 0,
  yMax = 1.0;
const sx = (t: number) => M.left + (t - xMin) / (xMax - xMin) * innerW;
const sy = (p: number) => M.top + innerH - (p - yMin) / (yMax - yMin) * innerH;
// Smooth path via Catmull-Rom-like cubic
function smoothPath(pts: {
  x: number;
  y: number;
}[]) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const t = 0.2;
    const c1x = p1.x + (p2.x - p0.x) * t;
    const c1y = p1.y + (p2.y - p0.y) * t;
    const c2x = p2.x - (p3.x - p1.x) * t;
    const c2y = p2.y - (p3.y - p1.y) * t;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}
const linePts = data.map(d => ({
  x: sx(d.t),
  y: sy(d.p)
}));
export function Figure6() {
  return <FigureCard number={6} title="Trapezoidal Rule Applied to P(t) Data at T_hot = 80 °C" subtitle="Each shaded trapezoid = energy produced in that 10-second interval. Sum of all 12 = E_out = 77.5 μJ." caption="Pedagogical illustration of the numerical integration method used to compute total electrical energy from discrete power measurements. The trapezoidal approximation is accurate to within ~1% compared with denser sampling." data-id="element-288">
      <div className="relative w-full overflow-x-auto" data-id="element-289">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet" data-id="element-290">
          {/* Grid lines */}
          {[0, 0.2, 0.4, 0.6, 0.8, 1.0].map(yt => <g key={yt} data-id="element-291">
              <line x1={M.left} x2={M.left + innerW} y1={sy(yt)} y2={sy(yt)} stroke="#E5E0D6" strokeDasharray="2 4" data-id="element-292" />
              <text x={M.left - 10} y={sy(yt) + 4} fontSize="11" textAnchor="end" fill="#374151" fontFamily="Inter" data-id="element-293">
                {yt.toFixed(2)}
              </text>
            </g>)}
          {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120].map(xt => <g key={xt} data-id="element-294">
              <line x1={sx(xt)} x2={sx(xt)} y1={M.top} y2={M.top + innerH} stroke="#F0EBE0" strokeDasharray="1 4" data-id="element-295" />
              <text x={sx(xt)} y={M.top + innerH + 18} fontSize="11" textAnchor="middle" fill="#374151" fontFamily="Inter" data-id="element-296">
                {xt}
              </text>
            </g>)}

          {/* Axis lines */}
          <line x1={M.left} y1={M.top} x2={M.left} y2={M.top + innerH} stroke="#1B1B1B" data-id="element-297" />
          <line x1={M.left} y1={M.top + innerH} x2={M.left + innerW} y2={M.top + innerH} stroke="#1B1B1B" data-id="element-298" />

          {/* Trapezoids */}
          {data.slice(0, -1).map((d, i) => {
          const next = data[i + 1];
          const isHighlight = i === 4; // 40-50s
          const fill = isHighlight ? '#EF9F27' : i % 2 === 0 ? '#1A4A68' : '#2E6B8E';
          const op = isHighlight ? 0.75 : i % 2 === 0 ? 0.35 : 0.55;
          const pts = `${sx(d.t)},${sy(0)} ${sx(d.t)},${sy(d.p)} ${sx(next.t)},${sy(next.p)} ${sx(next.t)},${sy(0)}`;
          return <g key={i} data-id="element-299">
                <polygon points={pts} fill={fill} fillOpacity={op} stroke={isHighlight ? '#B45309' : '#1A4A68'} strokeOpacity={isHighlight ? 0.9 : 0.3} strokeWidth={isHighlight ? 1.5 : 0.5} data-id="element-300" />
                {/* Area label inside trapezoid for some intervals */}
                {(i === 4 || i === 5 || i === 0 || i === 11) && <text x={(sx(d.t) + sx(next.t)) / 2} y={(sy(d.p) + sy(next.p)) / 2 + 10} fontSize="10" textAnchor="middle" fontFamily="Inter" fontWeight="600" fill={isHighlight ? '#7C2D12' : '#1A4A68'} data-id="element-301">
                    {trapAreas[i].toFixed(2)}
                  </text>}
              </g>;
        })}

          {/* Smooth curve overlay */}
          <path d={smoothPath(linePts)} stroke="#8B2500" strokeWidth="2.5" fill="none" data-id="element-302" />

          {/* Data points */}
          {linePts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="4" fill="#8B2500" stroke="#fff" strokeWidth="1.5" data-id="element-303" />)}

          {/* Peak vertical dashed line at t=50 */}
          <line x1={sx(50)} x2={sx(50)} y1={sy(0.865)} y2={M.top} stroke="#8B2500" strokeWidth="1" strokeDasharray="4 3" opacity="0.7" data-id="element-304" />
          <text x={sx(50) + 6} y={M.top + 12} fontSize="10" fontFamily="Inter" fill="#8B2500" fontWeight="600" data-id="element-305">
            Peak power: 0.865 μW
          </text>

          {/* Axis labels */}
          <text x={M.left + innerW / 2} y={H - 12} textAnchor="middle" fontSize="13" fontFamily="Inter" fill="#1B1B1B" fontWeight="500" data-id="element-306">
            Time, t (s)
          </text>
          <text x={20} y={M.top + innerH / 2} textAnchor="middle" fontSize="13" fontFamily="Inter" fill="#1B1B1B" fontWeight="500" transform={`rotate(-90, 20, ${M.top + innerH / 2})`} data-id="element-307">
            Instantaneous Power, P(t) (μW)
          </text>

          {/* Highlight callout */}
          <g data-id="element-308">
            <line x1={sx(45)} y1={sy(0.84)} x2={sx(45) + 110} y2={sy(0.84) - 70} stroke="#B45309" strokeWidth="1" data-id="element-309" />
            <rect x={sx(45) + 100} y={sy(0.84) - 130} width="240" height="64" fill="#FEF3C7" stroke="#EF9F27" data-id="element-310" />
            <text x={sx(45) + 110} y={sy(0.84) - 110} fontSize="10" fontFamily="Inter" fontWeight="700" fill="#7C2D12" data-id="element-311">
              Largest trapezoid · [40–50 s]
            </text>
            <text x={sx(45) + 110} y={sy(0.84) - 93} fontSize="10" fontFamily="JetBrains Mono" fill="#1B1B1B" data-id="element-312">
              area = (0.810 + 0.865)/2 × 10
            </text>
            <text x={sx(45) + 110} y={sy(0.84) - 78} fontSize="11" fontFamily="JetBrains Mono" fontWeight="700" fill="#7C2D12" data-id="element-313">
              = 8.38 μJ
            </text>
          </g>
        </svg>

        {/* Formula + comparison boxes */}
        <div className="grid grid-cols-3 gap-3 mt-4 mx-2" data-id="element-314">
          <div className="bg-white border-2 border-[#1A4A68] rounded-sm p-3" data-id="element-315">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#1A4A68] font-semibold mb-1" data-id="element-316">
              Trapezoidal Rule
            </div>
            <div className="font-mono text-[12px] text-[#1B1B1B] leading-snug" data-id="element-317">
              E<sub data-id="element-318">out</sub> = Σᵢ [(Pᵢ + Pᵢ₊₁)/2] × Δt
            </div>
            <div className="font-mono text-[10px] text-[#5B5448] mt-1" data-id="element-319">
              Δt = 10 s · 12 trapezoids
            </div>
            <div className="font-mono text-[12px] font-bold text-[#1A4A68] mt-1" data-id="element-320">
              E_out = 77.5 μJ
            </div>
          </div>
          <div className="bg-[#FEF3C7] border border-[#EF9F27] rounded-sm p-3" data-id="element-321">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#B45309] font-semibold mb-1" data-id="element-322">
              Method comparison
            </div>
            <div className="font-mono text-[11px] text-[#5B5448]" data-id="element-323">
              Simple avg: 69.9 μJ{' '}
              <span className="text-[#8B2500]" data-id="element-324">(−9.8%)</span>
            </div>
            <div className="font-mono text-[11px] text-[#1B1B1B] font-bold" data-id="element-325">
              Trapezoidal: 77.5 μJ ✓
            </div>
            <div className="font-serif italic text-[10px] text-[#5B5448] mt-1" data-id="element-326">
              More accurate for curved P(t).
            </div>
          </div>
          <div className="bg-[#1A4A68] text-white rounded-sm p-3 flex flex-col justify-center" data-id="element-327">
            <div className="font-sans text-[9px] uppercase tracking-wider opacity-80 mb-1" data-id="element-328">
              Total area
            </div>
            <div className="font-serif font-bold text-[18px]" data-id="element-329">
              E_out = 77.5 μJ
            </div>
            <div className="font-sans text-[10px] opacity-80 mt-1" data-id="element-330">
              Sum of 12 trapezoidal panels
            </div>
          </div>
        </div>
      </div>
    </FigureCard>;
}